<script setup lang="ts">
import { Chart } from "chart.js";
import { v4 as uuidv4 } from "uuid";
import { Filters } from "~/core/models/filters";
import { barChartComputeYAxisTicks } from "~/core/utils/barChartComputeYAxisTicks";
import { useFetchDtwinMetricsLifeCycleState } from "~/modules/dashboard/composables/useFetchDtwinMetricsLifeCycleState";
import { getLifeCycleStateProperties } from "~/modules/dtwin/utils/getLifeCycleStateProperties";

const props = defineProps<{
  organizationCode?: number;
  selectedSectorsCode: number[];
  userAssignedSectorsCodes: number[];
}>();

const { t, te } = useI18n();
const chartId = `chart-${uuidv4()}`;
const chartRef = ref<HTMLCanvasElement>();
const chartInstance = ref<Chart | undefined>();

const { setFilter, isLoading, results, error, fetchData } =
  useFetchDtwinMetricsLifeCycleState({
    organizationCode: props.organizationCode,
  });

const selectedSectorsComputed = computed(() => {
  if (props.selectedSectorsCode && props.selectedSectorsCode.length > 0) {
    return props.selectedSectorsCode.map((sectorCode) => sectorCode.toString());
  } else {
    return props.userAssignedSectorsCodes;
  }
});

async function loadData() {
  setFilter(Filters.SECTORS, selectedSectorsComputed.value);
  await fetchData();
}

const aggregatedData: ComputedRef<Record<string, number>> = computed(() => {
  if (results.value) {
    return Object.values(results.value).reduce(
      (acc, curr) => {
        Object.entries(curr).forEach(([key, value]) => {
          // group "lockedPreprovisioned" and "lockedRegistered" (and others starting with "locked") to only one key "locked"
          if (key.startsWith("locked")) {
            key = "locked";
          }
          acc[key] = (acc[key] ?? 0) + (value ?? 0);
        });
        return acc;
      },
      {} as Record<string, number>,
    );
  }

  return {};
});

function buildChart() {
  const chartCtx = chartRef.value?.getContext("2d");
  const yAxisConfiguration = barChartComputeYAxisTicks(aggregatedData.value);
  if (chartCtx) {
    chartInstance.value = new Chart(chartCtx, {
      type: "bar",
      data: {
        labels: Object.keys(aggregatedData.value).map(
          (key) => getLifeCycleStateProperties(key, t, te).label,
        ),
        datasets: [
          {
            label: "",
            data: Object.values(aggregatedData.value),
            backgroundColor: Object.keys(aggregatedData.value).map(
              (key) => getLifeCycleStateProperties(key).hexadecimal,
            ),
            barThickness: 32,
            borderRadius: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
          },
          y: {
            min: 0,
            max: yAxisConfiguration.max,
            ticks: {
              stepSize: yAxisConfiguration.stepSize,
            },
          },
        },
      },
    });
  }
}

watch(
  () => selectedSectorsComputed.value,
  async () => {
    await loadData();
    buildChart();
  },
);
onMounted(async () => {
  await loadData();
  buildChart();
});
</script>

<template>
  <dashboard-card
    :is-loading="isLoading"
    :error="error"
    :title="t('dashboard.deviceStates.title')"
    loader-container-class="mt-14 mb-14"
  >
    <canvas :id="chartId" ref="chartRef" height="190" />
  </dashboard-card>
</template>
